import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import Landmark from 'lucide-react/dist/esm/icons/landmark'
import Lock from 'lucide-react/dist/esm/icons/lock'
import Eye from 'lucide-react/dist/esm/icons/eye'
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check'
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2'
import Loader2 from 'lucide-react/dist/esm/icons/loader-2'
import { StripeConnectButton } from '@/components/dashboard/StripeConnectButton'
import { StripeWordmark } from '@/components/dashboard/StripeWordmark'
import { Input } from '@/components/ui/input'
import {
  completeBankSession,
  createBankSession,
  fetchBanking,
  setBankAccountVenueName,
} from '@/api/riskPortal.api'
import { ApiError } from '@/api/baseApi'
import type { RiskPortalBankAccount, RiskPortalBankingResponse } from '@/types/riskPortal.types'

export const BANKING_QUERY_KEY = ['portal-banking'] as const

const TRUST_POINTS = [
  {
    icon: Eye,
    title: 'Read-only',
    description: 'No payment or transfer permission is requested.',
  },
  {
    icon: ShieldCheck,
    title: 'No credentials',
    description: 'Mezza never sees your bank login details.',
  },
  {
    icon: CheckCircle2,
    title: 'You consent',
    description: 'You choose the accounts and data to share.',
  },
] as const

function connectErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const data = error.data as { detail?: string } | null
    if (data?.detail) return data.detail
  }
  return 'Bank connection failed. Please try again.'
}

/** Balance arrives from the backend in major units with the account's ISO currency code. */
function formatBalance(account: RiskPortalBankAccount): string | null {
  if (account.currentBalance === null || !account.currency) return null
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: account.currency.toUpperCase(),
    }).format(account.currentBalance)
  } catch {
    return `${account.currentBalance.toFixed(2)} ${account.currency.toUpperCase()}`
  }
}

function accountLabel(account: RiskPortalBankAccount): string {
  return account.displayName || account.institutionName || 'Bank account'
}

function LinkedAccountRow({
  account,
  onSaveVenueName,
  isSaving,
}: {
  account: RiskPortalBankAccount
  onSaveVenueName: (accountId: string, venueName: string) => void
  isSaving: boolean
}) {
  const balance = formatBalance(account)
  const name = accountLabel(account)
  const [draft, setDraft] = useState(account.venueName ?? '')

  useEffect(() => {
    setDraft(account.venueName ?? '')
  }, [account.venueName])

  const save = () => {
    const trimmed = draft.trim()
    if (trimmed === (account.venueName ?? '')) return
    onSaveVenueName(account.accountId, trimmed)
  }

  return (
    <li className="rounded-xl border border-border/50 bg-muted/20 px-3.5 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {name}
            {account.last4 ? <span className="text-muted-foreground"> •••• {account.last4}</span> : null}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {[account.institutionName, account.accountHolderNames.join(', ') || null]
              .filter(Boolean)
              .join(' — ')}
          </p>
        </div>
        {balance ? (
          <p className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{balance}</p>
        ) : null}
      </div>

      <div className="mt-2.5 border-t border-border/40 pt-2.5">
        <label htmlFor={`venue-name-${account.accountId}`} className="block">
          <span className="text-xs font-medium text-foreground">Venue name</span>
          <div className="relative mt-1.5">
            <Input
              id={`venue-name-${account.accountId}`}
              type="text"
              value={draft}
              maxLength={200}
              disabled={isSaving}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={save}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur()
              }}
              placeholder="Which venue does this account fund?"
              aria-label={`Venue name for ${name}`}
              className="h-9 rounded-lg bg-white pr-9"
            />
            {isSaving ? (
              <span
                className="pointer-events-none absolute inset-y-0 right-2.5 flex w-4 items-center justify-center"
                aria-hidden="true"
              >
                <span className="animate-spin">
                  <Loader2 className="h-4 w-4 text-muted-foreground" />
                </span>
              </span>
            ) : null}
          </div>
        </label>
      </div>
    </li>
  )
}

/**
 * Live Stripe Financial Connections entry point. Creates a single-use FC session on the backend,
 * opens the Stripe-hosted account picker (stripe-js is lazy-loaded on click so it stays out of the
 * initial bundle), then asks the backend to complete the session and ingest the linked accounts.
 * When banking is unavailable for the client's region the card stays informational with a disabled CTA.
 */
export function BankVerificationCard() {
  const queryClient = useQueryClient()
  const [isConnecting, setIsConnecting] = useState(false)
  const [savingVenueFor, setSavingVenueFor] = useState<string | null>(null)

  const { data: banking, isLoading } = useQuery({
    queryKey: BANKING_QUERY_KEY,
    queryFn: fetchBanking,
  })

  const available = banking?.available ?? false
  const isConnected = banking?.status === 'connected' && banking.accounts.length > 0

  const handleSaveVenueName = async (accountId: string, venueName: string) => {
    setSavingVenueFor(accountId)
    try {
      await setBankAccountVenueName(accountId, venueName)
      await queryClient.invalidateQueries({ queryKey: BANKING_QUERY_KEY })
    } catch (error: unknown) {
      toast.error(connectErrorMessage(error))
    } finally {
      setSavingVenueFor(null)
    }
  }

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      const session = await createBankSession()

      const { loadStripe } = await import('@stripe/stripe-js')
      const stripe = await loadStripe(session.publishableKey)
      if (!stripe) {
        toast.error('Could not load Stripe. Please try again.')
        return
      }

      const result = await stripe.collectFinancialConnectionsAccounts({
        clientSecret: session.clientSecret,
      })

      if (result.error) {
        toast.error(result.error.message ?? 'Bank connection failed. Please try again.')
        return
      }

      if (result.financialConnectionsSession.accounts.length === 0) {
        // The user closed the Stripe modal without linking anything — not an error.
        return
      }

      const updated = await completeBankSession(session.sessionId)
      queryClient.setQueryData<RiskPortalBankingResponse>(BANKING_QUERY_KEY, updated)
      toast.success('Bank account connected')
    } catch (error: unknown) {
      toast.error(connectErrorMessage(error))
    } finally {
      setIsConnecting(false)
    }
  }

  return (
    <div
      id="verification-bank-connection"
      tabIndex={-1}
      className="scroll-mt-24 overflow-hidden rounded-2xl border border-border/60 bg-white shadow-[0_2px_16px_rgba(0,0,0,0.04)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
    >
      <div className="border-b border-border/50 bg-gray-to-orange-gradient-small px-5 py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-sm">
              <Landmark className="h-5 w-5 text-primary" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-base font-semibold text-foreground">Bank verification</p>
                {isConnected ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-success-border/40 bg-success/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success-border">
                    <CheckCircle2 className="h-3 w-3" />
                    Connected
                  </span>
                ) : !available && !isLoading ? (
                  <span className="rounded-full border border-border/60 bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Not available in your region
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">Optional, and it speeds up your review</p>
            </div>
          </div>

          <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#635BFF]/20 bg-[#635BFF]/[0.06] px-2.5 py-1 text-xs sm:inline-flex">
            <StripeWordmark className="text-[#635BFF]" />
            <span className="text-muted-foreground">Financial Connections</span>
          </span>
        </div>
      </div>

      <div className="px-5 py-5">
        {isConnected ? (
          <>
            <h3 className="text-lg font-semibold leading-snug tracking-tight text-foreground">
              Your bank is connected
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              We have read-only access to the accounts below for your assessment. You can link
              additional accounts at any time. Enter a venue name for each account so we know which of
              your venues it belongs to.
            </p>

            <ul className="mt-4 space-y-2.5">
              {banking?.accounts.map((account) => (
                <LinkedAccountRow
                  key={account.accountId}
                  account={account}
                  onSaveVenueName={handleSaveVenueName}
                  isSaving={savingVenueFor === account.accountId}
                />
              ))}
            </ul>

            <StripeConnectButton
              variant="secondary"
              label="add-account"
              disabled={isConnecting}
              isLoading={isConnecting}
              onClick={() => void handleConnect()}
              className="mt-4"
            />
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold leading-snug tracking-tight text-foreground">
              Connect your bank securely through Stripe
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Instead of uploading statements, you can grant read-only access to the bank data we need
              for your assessment. You sign in directly with your bank through Stripe, which is faster
              and safer than sharing files.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {TRUST_POINTS.map(({ icon: Icon, title, description }) => (
                <div key={title} className="rounded-xl border border-border/50 bg-muted/20 p-3">
                  <div className="flex items-center gap-1.5">
                    <Icon className="h-4 w-4 text-success-border" />
                    <p className="text-sm font-semibold text-foreground">{title}</p>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
                </div>
              ))}
            </div>

            <StripeConnectButton
              disabled={!available || isLoading || isConnecting}
              isLoading={isConnecting}
              onClick={() => void handleConnect()}
              className="mt-5"
            />

            <div className="mt-3 space-y-1.5 text-center text-xs text-muted-foreground">
              <div className="flex items-center justify-center gap-1.5">
                <Lock className="h-3.5 w-3.5 shrink-0 text-[#635BFF]/80" />
                {available || isLoading ? (
                  <span>
                    Secured by{' '}
                    <StripeWordmark className="text-[#635BFF]" />{' '}
                    · read-only bank access
                  </span>
                ) : (
                  <span>
                    Bank connection is not available for your region yet — please upload bank statements
                    instead
                  </span>
                )}
              </div>
              {available || isLoading ? (
                <p>Opens a Stripe-hosted sign-in — Mezza never sees your bank login details.</p>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
